import apiClient from '../utils/apiClient';
import { handleApiError } from '../utils/errorHandler';

export interface SpecItem {
  _id: string;
  id?: string;
  nameFa: string;
  nameEn?: string;
  slug: string;
  description?: string;
  parent?:
    | string
    | { _id: string; nameFa: string; nameEn?: string; slug: string }
    | null;
  sortOrder: number;
  isActive: boolean;
  isDeleted?: boolean;
  attributes?: Record<string, any> | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ListResult {
  items: SpecItem[];
  total: number;
  page: number;
  limit: number;
}

export interface CreateSpecInput {
  type: string;
  nameFa: string;
  nameEn?: string;
  slug?: string;
  description?: string;
  parent?: string | null;
  sortOrder?: number;
  isActive?: boolean;
  attributes?: Record<string, any>;
}

const BASE = '/admin/specifications';

class SpecificationService {
  list(opts: {
    type: string;
    search?: string;
    parent?: string | null;
    activeOnly?: boolean;
    includeDeleted?: boolean;
    page?: number;
    limit?: number;
  }): Promise<ListResult> {
    const q = new URLSearchParams();
    q.set('type', opts.type);
    if (opts.search) q.set('search', opts.search);
    if (opts.parent === null) q.set('parent', 'null');
    else if (opts.parent) q.set('parent', opts.parent);
    if (opts.activeOnly) q.set('activeOnly', 'true');
    if (opts.includeDeleted) q.set('includeDeleted', 'true');
    if (opts.page) q.set('page', String(opts.page));
    if (opts.limit) q.set('limit', String(opts.limit));
    return apiClient
      .get(`${BASE}?${q.toString()}`)
      .then((r) => r.data?.data)
      .catch((e) => {
        handleApiError(e);
        throw e;
      });
  }

  get(type: string, id: string): Promise<SpecItem> {
    return apiClient
      .get(`${BASE}/${id}?type=${encodeURIComponent(type)}`)
      .then((r) => r.data?.data)
      .catch((e) => {
        handleApiError(e);
        throw e;
      });
  }

  create(input: CreateSpecInput): Promise<SpecItem> {
    return apiClient
      .post(BASE, input)
      .then((r) => r.data?.data)
      .catch((e) => {
        handleApiError(e);
        throw e;
      });
  }

  update(type: string, id: string, input: Partial<CreateSpecInput>): Promise<SpecItem> {
    return apiClient
      .put(`${BASE}/${id}?type=${encodeURIComponent(type)}`, input)
      .then((r) => r.data?.data)
      .catch((e) => {
        handleApiError(e);
        throw e;
      });
  }

  remove(type: string, id: string): Promise<void> {
    return apiClient
      .delete(`${BASE}/${id}?type=${encodeURIComponent(type)}`)
      .then(() => undefined)
      .catch((e) => {
        handleApiError(e);
        throw e;
      });
  }

  restore(type: string, id: string): Promise<SpecItem> {
    return apiClient
      .post(`${BASE}/${id}/restore?type=${encodeURIComponent(type)}`)
      .then((r) => r.data?.data)
      .catch((e) => {
        handleApiError(e);
        throw e;
      });
  }

  toggle(type: string, id: string): Promise<SpecItem> {
    return apiClient
      .patch(`${BASE}/${id}/toggle?type=${encodeURIComponent(type)}`)
      .then((r) => r.data?.data)
      .catch((e) => {
        handleApiError(e);
        throw e;
      });
  }

  reorder(type: string, updates: Array<{ id: string; sortOrder: number }>): Promise<void> {
    return apiClient
      .post(`${BASE}/reorder?type=${encodeURIComponent(type)}`, { updates })
      .then(() => undefined)
      .catch((e) => {
        handleApiError(e);
        throw e;
      });
  }
}

export default new SpecificationService();
